import { LOCAL_STORAGE_KEYS } from "../const/postAuthConsts";
import QueryFilter, { QueryFilterType, SimpleQueryFilterType } from "../utils/data/filters/QueryFilter";
import { DEFAULT_LOCALE_CODE } from "../utils/data/i18n/localeManager";
import { DEVICES_COLUMNS } from "../components/Pages/Devices/devicesColumns";
import { PRESET_TYPES } from "../components/sections/PresetButton/presetConsts";
import {
    AdminReduxSectionType,
    AlertsReduxSectionType,
    DashboardReduxSectionType,
    GeneralReduxSectionType,
    PoliciesReduxSectionType,
    ReportsReduxSectionType,
    RiskReduxSectionType,
    SettingsReduxSectionType,
    UserReduxSectionType,
} from "./ReduxStateType";

const presets = {} as DashboardReduxSectionType["presets"];
Object.values(PRESET_TYPES).forEach(presetType => {
    presets[presetType.redux] = {
        presets: undefined,
        default: undefined,
        current: undefined,
    };
});

export const dashboard: DashboardReduxSectionType = {
    deviceTypeFamiliesDistribution: undefined,
    deviceModelsDistribution: undefined,
    alertsTimeline: {
        y: [],
        x: [],
    },
    alertTypeDistribution: {
        securityAlerts: {},
        networkAlerts: {},
    },
    notificationConfiguration: undefined,
    labels: undefined,
    presets,
    allModels: undefined,
};

export const user: UserReduxSectionType = {
    keep_alive_interval: 300000,
    userDetails: {
        isLoaded: false, // TODO: remove this

        first_name: undefined,
        last_name: undefined,
        display_name: undefined,
        username: undefined,
        email: undefined,
        user_id: undefined,
        title: undefined,
        image: null,
        role_names: undefined,
        created_date: undefined,
        first_login: undefined,
        last_login: undefined,
        group_ids: [],
        client_ip: undefined, // used for analytics
        collector_public_key: undefined,

        // state
        is_activated: false,
        is_registration_mail_expired: false,

        // user types
        is_clone: false,
        is_api_user: false,
        has_api_token: false,
        logged_in_to_managing_organization: false, // unused

        // permissions
        is_superuser: false,
        permission_for_all_sites: false,
        permissions: {},

        // preferences
        user_preferences: {
            language: undefined,
            timezone: "America/New_York",
            locale_code: DEFAULT_LOCALE_CODE,
            is_24_hour_clock: undefined,
            display_kb_widget: true,
            theme: undefined,
        },
    },
    organizationDetails: {
        name: undefined,
        display_name: undefined,
        url: undefined,
        image: undefined,
        timezone: undefined,
        default_country_code: undefined,
        is_management_organization: false,
        managing_organization_name: undefined,
        active_risk_configuration_is_custom: false,
        active_likelihood_risk_configuration_is_custom: false,
        active_impact_risk_configuration_is_custom: false,
        has_enough_data_for_risk_benchmarks: false,
        imaging_examinations_show_operator_names: false,
        product_vertical: undefined,
        sfdc_medigate_id: undefined,
        is_federal: false,
        is_legacy_edge_key_exists: false,
        aws_region: undefined,
    },
    sessionData: {
        is_impersonated_role: false,
        temporarily_disabled_modules: [],
    },
    configurations: {
        devices_configuration: undefined,
        devicesCustomColumns: [],
    },
    siteDetails: [],

    // TODO: refactor - move to configurations
    photos_cfg: {
        picture_configuration: undefined,
    },
    notifications: undefined,

    // When not yet logged in (initial-config is loading), or we already logged-out,
    // set to undefined so that the preAuth routes will be used
    routes: undefined,
};

export const admin: AdminReduxSectionType = {
    users: undefined,
    defaultNotificationsConfiguration: undefined,
    role: undefined,
    permissions: undefined,
    login: {
        mfa: {
            method: undefined,
            duo_config: undefined,
        },
        password_login_enabled: undefined,
        sso: {
            enabled: undefined,
            providers: undefined,
        },
    },
};

export const alerts: AlertsReduxSectionType = {
    alertsData: undefined,
    specificAlert: undefined,
};

// This function should be removed after 2-3 releases, when all users will have the global filters in their local storage
const getInitialGlobalFilters = (): QueryFilterType => {
    let globalFilters = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.globalFilters));
    if (!globalFilters) return;
    globalFilters = QueryFilter.map(globalFilters, (filter: SimpleQueryFilterType) =>
        // eslint-disable-next-line no-restricted-syntax
        filter.field !== "sitev2_name"
            ? filter
            : {
                  ...filter,
                  field: DEVICES_COLUMNS.site_name.id,
              },
    );
    localStorage.setItem(LOCAL_STORAGE_KEYS.globalFilters, JSON.stringify(globalFilters));
    return globalFilters;
};

export const general: GeneralReduxSectionType = {
    // UI State
    connectedTooltipProps: {
        isTriggerElementHovered: false,
        triggerElement: undefined,
        tooltip: undefined,
        centerPlacement: false,
        placement: undefined,
        footer: undefined,
        header: undefined,
    },
    previousLocation: [],
    breadCrumbs: {
        isVisible: false,
        devicePageParams: undefined,
        pathParts: [],
    },
    serverExports: {
        processes: [
            // { name, socketId, startTime, status, statusParams: {} }
        ],
    },

    // Data
    insights: undefined,
    vulnerabilities: undefined,
    applications: undefined,
    protocols: undefined,
    patches: undefined,
    alertTypes: undefined,
    allAlertTypes: undefined,
    pathManagementIntegrationEnabled: undefined,
    patchesCount: 0,
    users: undefined,
    cache: {},
    cacheTimestamps: {},
    integrations: undefined,
    globalFilters: getInitialGlobalFilters(),
};

export const policies: PoliciesReduxSectionType = {
    policies: undefined,
    policyRecommendations: undefined,
    currentPolicyRules: undefined,
    currentPolicyRulesClean: undefined,
    observedModels: undefined,
};

export const risk: RiskReduxSectionType = { threatIntelNews: undefined };

export const reports: ReportsReduxSectionType = {
    data: {},
    errors: {},
};

export const settings: SettingsReduxSectionType = {
    integrations: undefined,
    supportedIntegrations: undefined,
    integrationCategories: [],
    fakeIntegrationsActivations: JSON.parse(localStorage.getItem("demo_integrations_activations") || "{}"),
    rules: [],
    ruleType: undefined,
    modules: undefined,
    showModulesBar: localStorage.getItem(LOCAL_STORAGE_KEYS.showModulesBar)
        ? localStorage.getItem(LOCAL_STORAGE_KEYS.showModulesBar) == "true"
        : true,
    setupConfigurationScore: undefined,
    ruleUid: undefined,
    groups: undefined,
};
